import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { todayString } from '../../utils/appDateTime';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import DateInput from '../ui/date-input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { Plus, RefreshCw, Eye } from 'lucide-react';
import AsyncSubmitButton, { ASYNC_SUBMIT_STATUS } from '../ui/AsyncSubmitButton';
import { INQUIRY_SOURCES, INQUIRY_STATUSES, labelFor, staffLabel, receptionApiErrorMessage } from './receptionConstants';
import { useReceptionStaff, useClasses } from './useReceptionData';
import MobileInput from '../ui/mobile-input';
import { validateMobileFields } from '../../utils/phoneValidation';
import ReceptionConvertDialog from './ReceptionConvertDialog';

const emptyForm = () => ({
  student_name: '', parent_name: '', father_name: '', mother_name: '',
  mobile_number: '', whatsapp_number: '', email: '', interested_class_id: '',
  previous_school: '', city: '', inquiry_source: 'walk_in',
  inquiry_date: todayString(), next_followup_date: '',
  assigned_user_id: '', status: 'new', remarks: '',
});

export default function ReceptionInquiries() {
  const { toast } = useToast();
  const { canManageFeature, hasPermission } = useAuth();
  const canManage = canManageFeature('reception') || hasPermission('manage_reception');
  const canConvert = hasPermission('convert_reception_inquiry') || hasPermission('manage_students') || canManageFeature('students');
  const staff = useReceptionStaff();
  const classes = useClasses();

  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [convertId, setConvertId] = useState(null);
  const [page, setPage] = useState(1);
  const [phoneErrors, setPhoneErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState(ASYNC_SUBMIT_STATUS.idle);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      const res = await api.get('/reception/inquiries', { params });
      setItems(res.data?.data?.items || []);
      setMeta(res.data?.data?.meta || { page: 1, pages: 1, total: 0 });
    } catch (e) {
      toast({ title: 'Error', description: receptionApiErrorMessage(e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, [page, statusFilter]);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchItems(); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const openCreate = () => {
    setEditing(null);
    setSubmitStatus(ASYNC_SUBMIT_STATUS.idle);
    setForm(emptyForm());
    setPhoneErrors({});
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setSubmitStatus(ASYNC_SUBMIT_STATUS.idle);
    setForm({
      ...emptyForm(),
      ...row,
      interested_class_id: row.interested_class_id ? String(row.interested_class_id) : '',
      assigned_user_id: row.assigned_user_id ? String(row.assigned_user_id) : '',
    });
    setPhoneErrors({});
    setOpen(true);
  };

  const save = async ({ beginLoading }) => {
    if (!form.student_name?.trim()) {
      toast({ title: 'Student name is required', variant: 'destructive' });
      return;
    }
    const phoneFieldErrors = validateMobileFields({
      mobile_number: form.mobile_number,
      whatsapp_number: form.whatsapp_number,
    });
    if (Object.keys(phoneFieldErrors).length > 0) {
      setPhoneErrors(phoneFieldErrors);
      const firstErr = Object.values(phoneFieldErrors)[0];
      toast({ title: 'Invalid mobile number', description: firstErr, variant: 'destructive' });
      return;
    }
    setPhoneErrors({});
    beginLoading();
    try {
      const payload = { ...form };
      if (editing) {
        await api.put(`/reception/inquiries/${editing.id}`, payload);
        toast({ title: 'Inquiry updated' });
      } else {
        await api.post('/reception/inquiries', payload);
        toast({ title: 'Inquiry created' });
      }
    } catch (e) {
      toast({ title: 'Error', description: receptionApiErrorMessage(e), variant: 'destructive' });
      throw e;
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this inquiry?')) return;
    try {
      await api.delete(`/reception/inquiries/${id}`);
      toast({ title: 'Deleted' });
      fetchItems();
    } catch (e) {
      toast({ title: 'Error', description: receptionApiErrorMessage(e), variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Admission Inquiries</h1>
          <p className="text-muted-foreground">Manage prospect admissions and follow-ups</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={fetchItems}><RefreshCw className="w-4 h-4" /></Button>
          {canManage && <Button onClick={openCreate}><Plus className="w-4 h-4 mr-2" />Add Inquiry</Button>}
        </div>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-wrap gap-3">
          <Input type="search" placeholder="Search name, phone, email…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
          <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-44"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {INQUIRY_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Inquiries ({meta.total})</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p className="text-muted-foreground">Loading…</p> : items.length === 0 ? (
            <p className="text-muted-foreground">No inquiries found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4">Student</th>
                    <th className="py-2 pr-4">Class</th>
                    <th className="py-2 pr-4">Source</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Next Follow-up</th>
                    <th className="py-2 pr-4">Counselor</th>
                    <th className="py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id} className="border-b border-border/50">
                      <td className="py-3 pr-4 font-medium">{row.student_name}</td>
                      <td className="py-3 pr-4">{row.class_name || '—'}</td>
                      <td className="py-3 pr-4">{labelFor(INQUIRY_SOURCES, row.inquiry_source)}</td>
                      <td className="py-3 pr-4">{labelFor(INQUIRY_STATUSES, row.status)}</td>
                      <td className="py-3 pr-4">{row.next_followup_date || '—'}</td>
                      <td className="py-3 pr-4">{row.assigned_to_name || '—'}</td>
                      <td className="py-3 flex flex-wrap gap-2">
                        <Button variant="ghost" size="sm" asChild><Link to={`/reception/inquiries/${row.id}`}><Eye className="w-4 h-4" /></Link></Button>
                        {canManage && <Button variant="outline" size="sm" onClick={() => openEdit(row)}>Edit</Button>}
                        {canConvert && row.status !== 'admission_confirmed' && (
                          <Button variant="secondary" size="sm" onClick={() => setConvertId(row.id)}>Convert</Button>
                        )}
                        {canManage && hasPermission('delete_reception') && (
                          <Button variant="destructive" size="sm" onClick={() => remove(row.id)}>Delete</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {meta.pages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <span className="self-center text-sm text-muted-foreground">Page {page} of {meta.pages}</span>
              <Button variant="outline" disabled={page >= meta.pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Inquiry' : 'New Inquiry'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="md:col-span-2"><Label>Student Name *</Label><Input value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })} /></div>
            <div><Label>Parent Name</Label><Input value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} /></div>
            <div><Label>Father Name</Label><Input value={form.father_name} onChange={(e) => setForm({ ...form, father_name: e.target.value })} /></div>
            <div><Label>Mother Name</Label><Input value={form.mother_name} onChange={(e) => setForm({ ...form, mother_name: e.target.value })} /></div>
            <div><Label>Mobile</Label><MobileInput value={form.mobile_number} onChange={(v) => { setForm({ ...form, mobile_number: v }); setPhoneErrors((prev) => ({ ...prev, mobile_number: undefined })); }} error={phoneErrors.mobile_number} /></div>
            <div><Label>WhatsApp</Label><MobileInput value={form.whatsapp_number} onChange={(v) => { setForm({ ...form, whatsapp_number: v }); setPhoneErrors((prev) => ({ ...prev, whatsapp_number: undefined })); }} error={phoneErrors.whatsapp_number} /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div><Label>Interested Class</Label>
              <Select value={form.interested_class_id || 'none'} onValueChange={(v) => setForm({ ...form, interested_class_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {classes.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Source</Label>
              <Select value={form.inquiry_source} onValueChange={(v) => setForm({ ...form, inquiry_source: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{INQUIRY_SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Inquiry Date</Label><DateInput value={form.inquiry_date} onChange={(v) => setForm({ ...form, inquiry_date: v })} /></div>
            <div><Label>Next Follow-up</Label><DateInput value={form.next_followup_date} onChange={(v) => setForm({ ...form, next_followup_date: v })} /></div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{INQUIRY_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Assigned Counselor</Label>
              <Select value={form.assigned_user_id || 'none'} onValueChange={(v) => setForm({ ...form, assigned_user_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Staff" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {staff.map((s) => <SelectItem key={s.id} value={String(s.id)}>{staffLabel(s)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label>Previous School</Label><Input value={form.previous_school} onChange={(e) => setForm({ ...form, previous_school: e.target.value })} /></div>
            <div className="md:col-span-2"><Label>Remarks</Label><Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitStatus !== ASYNC_SUBMIT_STATUS.idle}>Cancel</Button>
            <AsyncSubmitButton
              onAction={save}
              onSuccess={() => { setOpen(false); fetchItems(); }}
              onStatusChange={setSubmitStatus}
              loadingLabel="Saving…"
            >
              Save
            </AsyncSubmitButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {convertId && (
        <ReceptionConvertDialog
          inquiryId={convertId}
          onClose={() => setConvertId(null)}
          onSuccess={() => { setConvertId(null); fetchItems(); }}
        />
      )}
    </div>
  );
}
